import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = '/api';

const DAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const DAY_LABELS = {
  lundi: 'Lundi',
  mardi: 'Mardi',
  mercredi: 'Mercredi',
  jeudi: 'Jeudi',
  vendredi: 'Vendredi',
  samedi: 'Samedi',
};

const TIME_SLOTS = [
  { start: '08:00', end: '10:00' },
  { start: '10:00', end: '12:00' },
  { start: '12:00', end: '14:00' },
  { start: '14:00', end: '16:00' },
  { start: '16:00', end: '18:00' },
];

export default function ChildSchedule() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  };

  useEffect(() => {
    if (childId) loadSchedule();
  }, [childId]);

  const loadSchedule = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/parent/children/${childId}/schedule?academic_year=2026-2027`,
        axiosConfig
      );
      setData(res.data);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const getScheduleForSlot = (day, slot) => {
    if (!data?.schedules) return null;
    return data.schedules.find(
      (s) => s.day_of_week === day && s.start_time === slot.start
    );
  };

  const getSubjectColor = (subjectName) => {
    const colors = [
      'bg-blue-100 border-blue-400 text-blue-900',
      'bg-green-100 border-green-400 text-green-900',
      'bg-purple-100 border-purple-400 text-purple-900',
      'bg-yellow-100 border-yellow-400 text-yellow-900',
      'bg-pink-100 border-pink-400 text-pink-900',
      'bg-indigo-100 border-indigo-400 text-indigo-900',
      'bg-red-100 border-red-400 text-red-900',
      'bg-teal-100 border-teal-400 text-teal-900',
    ];
    let hash = 0;
    for (let i = 0; i < (subjectName || '').length; i++) {
      hash = subjectName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading)
    return <div className="p-6 text-center text-gray-500">Chargement...</div>;
  if (error)
    return (
      <div className="p-6 bg-red-100 border border-red-400 text-red-700 rounded">
        {error}
      </div>
    );

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/parent/children')}
          className="text-sm text-blue-600 hover:underline mb-2"
        >
          ← Retour aux enfants
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          📅 Emploi du temps de {data?.student_name}
        </h1>
        <p className="text-gray-500 mt-1">
          {data?.class_name} — Année {data?.academic_year}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700 w-24">
                Heure
              </th>
              {DAYS.map((d) => (
                <th
                  key={d}
                  className="border border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700"
                >
                  {DAY_LABELS[d]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TIME_SLOTS.map((slot) => (
              <tr key={slot.start}>
                <td className="border border-gray-200 px-3 py-3 text-xs font-mono text-gray-600 bg-gray-50 text-center">
                  {slot.start}
                  <br />↓<br />
                  {slot.end}
                </td>
                {DAYS.map((day) => {
                  const schedule = getScheduleForSlot(day, slot);
                  const isPause = slot.start === '12:00';

                  if (isPause && !schedule) {
                    return (
                      <td
                        key={day}
                        className="border border-gray-200 px-2 py-4 text-center text-xs text-gray-400 bg-gray-50"
                      >
                        PAUSE
                      </td>
                    );
                  }

                  return (
                    <td
                      key={day}
                      className="border border-gray-200 px-1 py-1 align-top"
                      style={{ minWidth: '130px' }}
                    >
                      {schedule ? (
                        <div
                          className={`rounded border-l-4 p-2 text-xs ${getSubjectColor(
                            schedule.subject_name
                          )}`}
                        >
                          <div className="font-bold">{schedule.subject_name}</div>
                          <div className="text-[11px] opacity-80">
                            👨‍🏫 {schedule.teacher_name}
                          </div>
                          {schedule.room && (
                            <div className="text-[11px] opacity-80">
                              🚪 {schedule.room}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-full h-full min-h-[60px]"></div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data?.schedules?.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-8 rounded text-center mt-4">
          Aucun cours programmé pour le moment
        </div>
      )}
    </div>
  );
}